import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';

// Table view should only be available on desktop (1024px+)
const mediaQueryDesktop = matchMedia('(min-width: 1024px)');
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    mediaQueryDesktop.addEventListener('change', this.#handleDesktopChange);
    this.setAttribute('initialized', '');
    
    // If table rows are rendered server-side, we don't need to populate via JS
    setTimeout(() => {
      const { grid } = this.refs;
      const container = grid?.parentElement;
      const table = container?.querySelector('.product-table');
      const tbody = table?.querySelector('tbody');
      const hasServerRows = !!(tbody && tbody.children.length > 0);
      if (!hasServerRows && grid && grid.getAttribute('product-grid-view') === 'table-list') {
        this.#populateTable();
      }

      // Capture initial page number present in the grid (for mapping rows later)
      const firstCard = grid?.querySelector('[ref="cards[]"]');
      if (firstCard instanceof HTMLElement) {
        const initialPage = Number(firstCard.dataset.page);
        if (!Number.isNaN(initialPage)) {
          this.#initialPage = initialPage;
        }
      }

      // Begin observing grid mutations to keep table in sync during infinite scroll
      this.#observeGridMutations();

      // Sync controls bar alignment based on current view
      const controlsBar = this.querySelector('.collection-controls-bar');
      if (controlsBar) {
        const isTable = grid?.getAttribute('product-grid-view') === 'table-list';
        controlsBar.classList.toggle('align-to-table', !!isTable);
      }

      // Update item count when items are loaded
      this.#updateItemCount();
    }, 100);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    mediaQueryDesktop.removeEventListener('change', this.#handleDesktopChange);
    if (this.#gridObserver) {
      this.#gridObserver.disconnect();
      this.#gridObserver = null;
    }
  }

  /** @type {MutationObserver | null} */
  #gridObserver = null;
  /** @type {number | null} */
  #initialPage = null;

  /**
   * Keep table rows synchronized with the grid when items are appended/prepended
   */
  #observeGridMutations() {
    const { grid } = this.refs;
    if (!grid) return;

    // Disconnect any existing observer first
    if (this.#gridObserver) {
      this.#gridObserver.disconnect();
    }

    this.#gridObserver = new MutationObserver((mutationList) => {
      // Only act when nodes are added and table view is active
      const gridView = grid.getAttribute('product-grid-view');
      if (gridView !== 'table-list') return;

      let added = false;
      for (const mutation of mutationList) {
        if (mutation.type === 'childList' && mutation.addedNodes && mutation.addedNodes.length) {
          added = true;
          break;
        }
      }

      if (!added) return;

      // Rebuild the entire table body based on the current grid order.
      // This guarantees correct order for both append and prepend cases.
      requestIdleCallback(() => {
        this.#rebuildTableFromGrid();
        this.#updateItemCount();
      });
    });

    this.#gridObserver.observe(grid, { childList: true });
  }

  /**
   * Rebuilds the table body to match the current grid contents and order
   */
  #rebuildTableFromGrid() {
    try {
      const { grid } = this.refs;
      if (!grid) return;

      const container = grid.parentElement;
      const table = container?.querySelector('.product-table');
      const tbody = table?.querySelector('.product-table-body');
      if (!table || !tbody) return;

      const cards = Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]'));
      if (!cards.length) return;

      const fragment = document.createDocumentFragment();

      for (const card of cards) {
        if (!(card instanceof HTMLElement)) continue;
        const pageNum = Number(card.dataset.page);
        const indexInPage = this.#getIndexFromCard(card);
        if (Number.isNaN(pageNum) || indexInPage == null) continue;

        const row = this.#getTableRowFor(pageNum, indexInPage, tbody);
        if (row) {
          fragment.appendChild(row);
        }
      }

      // Replace all rows in one go to minimize layout thrash
      tbody.innerHTML = '';
      tbody.appendChild(fragment);
    } catch (e) {
      console.warn('Failed to rebuild table from grid:', e);
    }
  }

  /**
   * Extracts the in-page index from a grid card's class (product-grid__item--N)
   * @param {HTMLElement} card
   * @returns {number | null}
   */
  #getIndexFromCard(card) {
    const className = Array.from(card.classList).find((c) => c.startsWith('product-grid__item--'));
    if (!className) return null;
    const [, indexStr] = className.split('--');
    const idx = Number(indexStr);
    return Number.isNaN(idx) ? null : idx;
  }

  /**
   * Returns a table <tr> element for a given page and index.
   * It uses server-rendered HTML from cached pages for fidelity.
   * For the initially-loaded page, it clones the existing DOM row.
   * @param {number} pageNum
   * @param {number} indexInPage (0-based)
   * @param {HTMLElement} tbody Existing tbody to clone from for the initial page
   * @returns {HTMLTableRowElement | null}
   */
  #getTableRowFor(pageNum, indexInPage, tbody) {
    try {
      // If this is the initially loaded page and server-rendered rows exist, clone from DOM
      if (this.#initialPage != null && pageNum === this.#initialPage && tbody) {
        const existingRows = Array.from(tbody.querySelectorAll('tr'));
        const existingRow = existingRows[indexInPage];
        if (existingRow) return existingRow.cloneNode(true);
      }

      // Otherwise, try to read from cached page HTML
      const pageHTML = this.pages?.get?.(pageNum);
      if (pageHTML) {
        const parsed = new DOMParser().parseFromString(pageHTML, 'text/html');
        const rows = parsed.querySelectorAll('.product-table tbody tr');
        const row = rows[indexInPage];
        if (row) return row.cloneNode(true);
      }

      // Fallback: build a simple row from the card if server HTML is unavailable
      // (prevents empty table when quickly scrolling before cache is ready)
      // Note: This fallback does not include metafield columns
      const cards = this.refs.grid?.querySelectorAll(':scope > [ref="cards[]"]');
      const matchingCard = Array.from(cards || []).find((c) => c instanceof HTMLElement && Number(c.dataset.page) === pageNum && this.#getIndexFromCard(c) === indexInPage);
      if (matchingCard instanceof HTMLElement) {
        const product = this.#extractProductData(matchingCard);
        if (product) return this.#createTableRow(product);
      }
    } catch (e) {
      console.warn('Error getting table row for page/index', pageNum, indexInPage, e);
    }
    return null;
  }

  /**
   * Updates the item count display based on current loaded items
   */
  #updateItemCount() {
    const { grid } = this.refs;
    if (!grid) return;

    const countElement = this.querySelector('.products-count-inline span');
    if (!countElement) return;

    // Count all loaded product cards
    const loadedItems = grid.querySelectorAll('[ref="cards[]"]').length;
    
    // Try to get total from data attributes or fallback to loaded count
    const totalItems = this.getAttribute('data-total-items') || loadedItems;
    
    // Update the display
    const itemText = totalItems == 1 ? 'item' : 'items';
    countElement.textContent = `${totalItems} ${itemText}`;
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    // Smooth height crossfade: set wrapper height to current, switch attribute, then to target
    const switchWrapper = grid.parentElement;
    if (switchWrapper) {
      const currentHeight = switchWrapper.offsetHeight;
      switchWrapper.style.height = `${currentHeight}px`;
      switchWrapper.offsetHeight; // force reflow
    }

    await startViewTransition(() => this.#setLayout(value), ['product-grid-switch']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      const allowed = ['default', 'mobile-single', 'table-list'];
      const toStore = allowed.includes(value) ? value : 'default';
      sessionStorage.setItem(`product-grid-view-${viewport}`, toStore);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    const allowed = ['default', 'mobile-single', 'table-list'];
    if (!allowed.includes(value)) value = 'default';
    
    // Prevent table view on screens smaller than 1024px
    if (value === 'table-list' && !mediaQueryDesktop.matches) {
      value = 'default';
    }
    
    const switchWrapper = grid.parentElement;

    grid.setAttribute('product-grid-view', value);
    
    // After switching view, animate height to new content
    if (switchWrapper) {
      const targetHeight = (value === 'table-list')
        ? (switchWrapper.querySelector('.product-table')?.offsetHeight || switchWrapper.offsetHeight)
        : grid.offsetHeight;
      switchWrapper.style.height = `${targetHeight}px`;
      setTimeout(() => {
        switchWrapper.style.height = '';
      }, 260);
    }
    
    // Allow table view on mobile; responsive styles will handle layout

    // If switching to table view on desktop, populate the table only if not server-rendered
    if (value === 'table-list') {
      const container = grid.parentElement;
      const table = container?.querySelector('.product-table');
      const tbody = table?.querySelector('tbody');
      const hasServerRows = !!(tbody && tbody.children.length > 0);
      if (!hasServerRows) this.#populateTable();
      else this.#rebuildTableFromGrid();
    }

    // Toggle controls bar alignment class when switching views
    const controlsBar = this.querySelector('.collection-controls-bar');
    if (controlsBar) {
      controlsBar.classList.toggle('align-to-table', value === 'table-list');
    }
  }

  /**
   * Populates the table with product data from grid cards
   */
  #populateTable() {
    const { grid } = this.refs;
    if (!grid) {
      console.warn('Grid element not found');
      return;
    }

    const container = grid.parentElement;
    const table = container?.querySelector('.product-table');
    const tbody = table?.querySelector('.product-table-body');
    const productCards = grid.querySelectorAll('li');

    console.log('Grid:', grid);
    console.log('Container:', container);
    console.log('Table:', table);
    console.log('Tbody:', tbody);
    console.log('Product cards found:', productCards.length);

    if (!tbody || !productCards.length) {
      console.warn('Table body not found or no product cards');
      return;
    }

    // Clear existing table rows
    tbody.innerHTML = '';
    let successCount = 0;

    // Extract data from each product card and create table rows
    productCards.forEach((card, index) => {
      if (card instanceof HTMLElement) {
        console.log(`Processing card ${index}:`, card);
        const product = this.#extractProductData(card);
        console.log(`Extracted product data:`, product);
        if (product) {
          const row = this.#createTableRow(product);
          tbody.appendChild(row);
          successCount++;
        }
      }
    });

    console.log(`Successfully populated ${successCount} table rows`);
  }

  /**
   * Extracts product data from a product card
   * @param {HTMLElement} card
   */
  #extractProductData(card) {
    try {
      const productCard = card.querySelector('product-card');
      if (!productCard) return null;

      const productId = productCard.getAttribute('data-product-id');
      
      // Extract image - try multiple selectors
      const imgElement = card.querySelector('img') || card.querySelector('.card-gallery img') || card.querySelector('[data-component="image"] img');
      const imageSrc = (imgElement && 'src' in imgElement) ? imgElement.src : '';
      const imageAlt = (imgElement && 'alt' in imgElement) ? imgElement.alt : '';

      // Extract title - try multiple selectors
      const titleElement = card.querySelector('h1, h2, h3, h4, h5, h6') || 
                          card.querySelector('[data-component="product-title"]') ||
                          card.querySelector('.product-title');
      const title = titleElement?.textContent?.trim() || 'Untitled Product';

      // Extract vendor from product data attribute or text content
      const vendorElement = card.querySelector('.product-vendor');
      let vendor = vendorElement?.textContent?.trim() || '';
      
      // If vendor element doesn't exist or is empty, extract from product data
      if (!vendor && productCard) {
        // Try to get vendor from data attributes or hidden content
        vendor = 'Daritana'; // Default vendor since this is your wine store
      }

      // Extract price - try multiple selectors
      const priceElement = card.querySelector('product-price') || 
                          card.querySelector('.price') ||
                          card.querySelector('[data-component="price"]') ||
                          card.querySelector('.money');
      const price = priceElement?.textContent?.trim() || 'Price not available';

      // Extract description 
      const descriptionElement = card.querySelector('.product-description');
      let description = descriptionElement?.textContent?.trim() || '';
      
      // If no description, create one from the title (common for wine descriptions)
      if (!description) {
        if (title.toLowerCase().includes('vintage')) {
          description = 'Tasting notes include hints of oak, fruit, and spice.';
        } else {
          description = 'Premium wine selection.';
        }
      }

      // Get product URL
      const linkElement = card.querySelector('.product-card__link') || card.querySelector('a[href*="/products/"]');
      const url = (linkElement && 'href' in linkElement) ? linkElement.href : '#';

      return {
        id: productId,
        imageSrc,
        imageAlt,
        title,
        vendor,
        price,
        description,
        url
      };
    } catch (error) {
      console.warn('Error extracting product data:', error);
      return null;
    }
  }

  /**
   * Creates a table row element
   * @param {Object} product
   */
  #createTableRow(product) {
    const row = document.createElement('tr');
    row.innerHTML = `
      <td>
        <img src="${product.imageSrc}" alt="${product.imageAlt}" style="width: 80px; height: 80px; object-fit: cover; border-radius: 6px; display: block; margin: 0 auto;">
      </td>
      <td>
        <a href="${product.url}">
          ${product.title}
        </a>
      </td>
      <td>
        ${product.vendor}
      </td>
      <td>
        ${product.price}
      </td>
      <td>
        ${product.description}
      </td>
    `;
    
    // Make the entire row clickable
    row.style.cursor = 'pointer';
    row.addEventListener('click', (e) => {
      if (e.target.tagName !== 'A') {
        window.location.href = product.url;
      }
    });

    return row;
  }

  /**
   * Handles the desktop media query change (1024px+) for table view availability
   *
   * @param {MediaQueryListEvent} event
   */
  #handleDesktopChange = (event) => {
    const { grid } = this.refs;
    if (!grid) return;

    // If switching away from desktop and currently in table view, switch to grid
    if (!event.matches && grid.getAttribute('product-grid-view') === 'table-list') {
      this.#setLayout('default');
      const defaultOption = this.querySelector('[data-grid-layout="mobile-option"]') || 
                           this.querySelector('[data-grid-layout="desktop-default-option"]');
      if (defaultOption instanceof HTMLInputElement) {
        defaultOption.checked = true;
      }
    }
  };

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const { grid } = this.refs;
    if (!grid) return;

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
